<template>
  <v-dialog max-width="1100px" v-model="showDialog" persistent>
    <v-card>
      <!-- Header -->
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-map-marker-path</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Change Destination</span>
        <v-spacer />
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Order Info Strip -->
      <div class="d-flex align-center flex-wrap px-5 py-2" style="background:#eef2fb;border-bottom:1px solid #d5ddf0;gap:24px">
        <div v-if="orderInfo.eToken" class="info-item d-flex align-center">
          <v-icon x-small color="primary" class="mr-1">mdi-identifier</v-icon>
          <span class="info-label mr-1">Token:</span>
          <span class="info-value">{{ orderInfo.eToken }}</span>
        </div>
        <div v-if="orderInfo.customerName" class="info-item d-flex align-center">
          <v-icon x-small color="primary" class="mr-1">mdi-account-outline</v-icon>
          <span class="info-label mr-1">Customer:</span>
          <span class="info-value">{{ orderInfo.customerName }}</span>
        </div>
      </div>

      <v-card-text class="px-6 pt-5 pb-2">

        <!-- Per-container lane selection -->
        <div class="section-block mb-4">
          <div class="section-header d-flex align-center mb-3">
            <v-icon small color="primary" class="mr-1">mdi-package-variant</v-icon>
            <span class="text-caption font-weight-bold text-uppercase primary--text">Container Lane Assignment</span>
          </div>

          <table class="rates-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Container No</th>
                <th>Type / Size</th>
                <th>Current Lane</th>
                <th>New Lane</th>
                <th>Customer Rate</th>
                <th>Customer Contract</th>
                <template v-if="orderInfo.isSubHire">
                  <th>Vendor Rate</th>
                  <th>Vendor Contract</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in containerRows" :key="i">
                <td>{{ i + 1 }}</td>
                <td class="font-weight-bold primary--text">{{ row.container_no || '—' }}</td>
                <td>{{ row.type_name || '—' }}</td>
                <td class="grey--text">{{ row.current_lane || '—' }}</td>
                <td style="min-width:220px">
                  <Autocomplete
                    :label="'Lane'"
                    :items="laneLists[i] || []"
                    itemText="lane_title"
                    itemValue="id"
                    :loading="laneLoadings[i]"
                    :totalItems="laneCounts[i] || 0"
                    hide-details="auto"
                    dense
                    :value="containerLanes[i]"
                    @input="(val) => onContainerLaneChange(i, val)"
                    @updateList="(params) => fetchLanesForContainer(i, params)"
                  />
                </td>
                <!-- Customer rate -->
                <td>
                  <span v-if="containerRates[i]" :class="containerRates[i].found ? 'rate-chip' : 'rate-chip rate-chip--none'">
                    {{ containerRates[i].rate }}
                  </span>
                  <v-progress-circular v-else-if="rateLoadings[i]" indeterminate size="14" width="2" color="primary" />
                  <span v-else class="grey--text text-caption">—</span>
                </td>
                <td>
                  <v-icon v-if="containerRates[i]" x-small :color="containerRates[i].found ? 'success' : 'error'">
                    {{ containerRates[i].found ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  <span v-else class="grey--text text-caption">—</span>
                </td>
                <!-- Vendor rate (sub-hire only) -->
                <template v-if="orderInfo.isSubHire">
                  <td>
                    <span v-if="vendorRates[i]" :class="vendorRates[i].found ? 'rate-chip' : 'rate-chip rate-chip--none'">
                      {{ vendorRates[i].rate }}
                    </span>
                    <v-progress-circular v-else-if="rateLoadings[i]" indeterminate size="14" width="2" color="primary" />
                    <span v-else class="grey--text text-caption">—</span>
                  </td>
                  <td>
                    <v-icon v-if="vendorRates[i]" x-small :color="vendorRates[i].found ? 'success' : 'error'">
                      {{ vendorRates[i].found ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                    <span v-else class="grey--text text-caption">—</span>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Current Order Details -->
        <div class="section-block mt-3">
          <div class="section-header d-flex align-center mb-3">
            <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
            <span class="text-caption font-weight-bold text-uppercase primary--text">Current Order Details</span>
          </div>
          <v-row dense>
            <v-col cols="4">
              <div class="detail-label">DA(s)</div>
              <div class="detail-value">{{ daListText || '—' }}</div>
            </v-col>
            <v-col cols="4">
              <div class="detail-label">Trip(s)</div>
              <div class="detail-value">{{ tripListText || '—' }}</div>
            </v-col>
            <v-col cols="4">
              <div class="detail-label">Legs</div>
              <div class="detail-value">{{ legText || '—' }}</div>
            </v-col>
          </v-row>
        </div>

        <!-- Warning Banner -->
        <v-alert type="warning" dense outlined class="mt-4 mb-0" icon="mdi-alert-outline">
          Changing the lane will update the
          <strong>DA contract &amp; rates</strong>,
          <strong>leg source/destination</strong>, and
          <strong>trip route</strong>.
        </v-alert>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2">Cancel</v-btn>
        <v-btn
          color="warning darken-1"
          depressed
          :disabled="!hasAnyLaneSelected || hasAnyRateLoading || hasAnyContractMissing"
          min-width="180"
          @click="confirmDialog = true"
        >
          <v-icon small class="mr-1">mdi-swap-horizontal</v-icon>
          Change Destination
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="warning darken-1 white--text py-3 px-5">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          Confirm Change
        </v-card-title>
        <v-card-text class="pt-5 pb-3 px-5">
          <p class="text-body-2 mb-2">The following container lanes will be updated:</p>
          <ul class="text-body-2 mb-2">
            <li v-for="(row, i) in selectedContainerRows" :key="i">
              <strong>{{ row.container_no }}</strong>: {{ row.current_lane || '—' }} → <strong>{{ getLaneName(row.index) }}</strong>
            </li>
          </ul>
          <p class="text-body-2 mb-0">This will update the <strong>DA contract &amp; rates</strong>, <strong>leg route details</strong>, and <strong>trip route</strong>. This action cannot be undone.</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn depressed outlined @click="confirmDialog = false" class="mr-2">Cancel</v-btn>
          <v-btn color="warning darken-1" depressed :loading="submitting" min-width="120" @click="submit">
            <v-icon small class="mr-1">mdi-check</v-icon>
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";

export default {
  components: { Autocomplete },
  props: {
    value: { type: Boolean, default: false },
    orderId: { type: Number, default: null },
    orderInfo: { type: Object, default: () => ({}) },
  },
  watch: {
    value(val) {
      if (val) this.init();
    },
  },
  data() {
    return {
      containerLanes: {},   // index → selected lane object
      laneLists: {},        // index → lane list array
      laneCounts: {},       // index → total count
      laneLoadings: {},     // index → boolean
      containerRates: {},   // index → { rate, found }
      vendorRates: {},      // index → { rate, found } for sub-hire
      rateLoadings: {},     // index → boolean
      confirmDialog: false,
      submitting: false,
    };
  },
  computed: {
    showDialog: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },
    containerRows() {
      const daLaneList = this.orderInfo.daLaneList || [];
      // Build a map: container_no → { lane_title, lane_id }
      const containerLaneMap = {};
      for (const da of daLaneList) {
        for (const cno of (da.containers || [])) {
          containerLaneMap[(cno || '').toUpperCase()] = { lane_title: da.lane_title, lane_id: da.lane_id };
        }
      }
      return (this.orderInfo.containerData || []).map((c) => {
        const cno = (c.container_no || c.container_number || '').toUpperCase();
        const laneInfo = containerLaneMap[cno];
        return {
          container_no: c.container_no || c.container_number || "",
          type_name: c.type_name || c.container_type_name || "",
          container_type: c.container_type,
          current_lane: laneInfo ? laneInfo.lane_title : (this.orderInfo.currentLane || null),
          current_lane_id: laneInfo ? laneInfo.lane_id : (this.orderInfo.laneId || null),
        };
      });
    },
    daListText() {
      return (this.orderInfo.daList || []).map((d) => d.da_no).join(", ");
    },
    tripListText() {
      return (this.orderInfo.tripList || []).map((t) => t.trip_id).join(", ") || this.orderInfo.tripId || null;
    },
    legText() {
      return (this.orderInfo.legIds || []).join(", ");
    },
    selectedContainerRows() {
      return this.containerRows
        .map((row, i) => ({ ...row, index: i }))
        .filter((row) => this.containerLanes[row.index]);
    },
    hasAnyLaneSelected() {
      return Object.values(this.containerLanes).some((l) => l);
    },
    hasAnyRateLoading() {
      return Object.values(this.rateLoadings).some(Boolean);
    },
    hasAnyContractMissing() {
      const customerMissing = Object.entries(this.containerRates).some(([, r]) => r && !r.found);
      if (customerMissing) return true;
      if (this.orderInfo.isSubHire) {
        return Object.entries(this.vendorRates).some(([, r]) => r && !r.found);
      }
      return false;
    },
  },
  methods: {
    init() {
      this.containerLanes = {};
      this.laneLists = {};
      this.laneCounts = {};
      this.laneLoadings = {};
      this.containerRates = {};
      this.vendorRates = {};
      this.rateLoadings = {};
      this.confirmDialog = false;
      this.containerRows.forEach((row, i) => {
        this.$set(this.laneLists, i, []);
        this.$set(this.laneLoadings, i, false);
        this.$set(this.rateLoadings, i, false);
        // Pre-select current lane
        if (row.current_lane_id) {
          this.$set(this.containerLanes, i, { id: row.current_lane_id, lane_title: row.current_lane });
        }
        this.fetchLanesForContainer(i, { limit: 10, offset: 0 });
      });
    },
    closeDialog() {
      this.showDialog = false;
    },
    fetchLanesForContainer(i, params = {}) {
      this.$set(this.laneLoadings, i, true);
      const sourceFilter = this.orderInfo.sourceLocationId
        ? { source_location: this.orderInfo.sourceLocationId }
        : {};
      this.$api.lane
        .getLaneList({ limit: 10, ...sourceFilter, ...params })
        .then((res) => {
          this.$set(this.laneLists, i, params.offset ? [...(this.laneLists[i] || []), ...res.data] : res.data);
          this.$set(this.laneCounts, i, res.count);
          const row = this.containerRows[i];
          if (row && row.current_lane_id && !params.offset) {
            const exists = (this.laneLists[i] || []).find((l) => l.id === row.current_lane_id);
            if (!exists) {
              this.laneLists[i].unshift({ id: row.current_lane_id, lane_title: row.current_lane });
            }
          }
        })
        .finally(() => { this.$set(this.laneLoadings, i, false); });
    },
    onContainerLaneChange(i, val) {
      this.$set(this.containerLanes, i, val);
      const laneId = val && typeof val === "object" ? val.id : val;
      if (!laneId) { this.$set(this.containerRates, i, null); return; }
      this.fetchRateForContainer(i, laneId);
    },
    fetchRateForContainer(i, laneId) {
      this.$set(this.rateLoadings, i, true);
      this.$set(this.containerRates, i, null);
      this.$set(this.vendorRates, i, null);
      const container = this.containerRows[i];
      this.$api.emergencyOrder
        .previewDestinationRates(this.orderId, laneId)
        .then((res) => {
          const rates = res.data?.rates || [];
          const typeId = container.container_type;
          const typeName = container.type_name;
          const rateObj = rates.find((r) => r.container_type_id === typeId) || rates.find((r) => r.container_type_name === typeName);
          this.$set(this.containerRates, i, rateObj ? { rate: rateObj.rate, found: rateObj.found } : { rate: "0.00", found: false });
          // Vendor contract (sub-hire only)
          if (this.orderInfo.isSubHire) {
            const vc = res.data?.vendor_contract;
            if (vc) {
              const vRateObj = (vc.rates || []).find((r) => r.container_type_id === typeId) || (vc.rates || [])[0];
              this.$set(this.vendorRates, i, vRateObj ? { rate: vRateObj.rate, found: vRateObj.found } : { rate: "0.00", found: false });
            } else {
              this.$set(this.vendorRates, i, { rate: "0.00", found: false });
            }
          }
        })
        .catch(() => {
          this.$set(this.containerRates, i, { rate: "0.00", found: false });
          if (this.orderInfo.isSubHire) this.$set(this.vendorRates, i, { rate: "0.00", found: false });
        })
        .finally(() => { this.$set(this.rateLoadings, i, false); });
    },
    getLaneName(i) {
      const lane = this.containerLanes[i];
      if (!lane) return "";
      if (typeof lane === "object") return lane.lane_title;
      const found = (this.laneLists[i] || []).find((l) => l.id === lane);
      return found ? found.lane_title : "";
    },
    submit() {
      this.submitting = true;
      // Build per-container lane map: container_no → lane_id
      const containerLaneMap = {};
      this.containerRows.forEach((row, i) => {
        const lane = this.containerLanes[i];
        if (lane) {
          const laneId = typeof lane === "object" ? lane.id : lane;
          containerLaneMap[row.container_no] = laneId;
        }
      });
      // If all containers share same lane, send as single lane (backward compat)
      const laneIds = [...new Set(Object.values(containerLaneMap))];
      const payload = laneIds.length === 1
        ? { lane: laneIds[0] }
        : { container_lane_map: containerLaneMap };

      this.$api.emergencyOrder
        .changeDestination(this.orderId, payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", { message: "Destination changed successfully!", color: "success" });
          this.$emit("destinationChanged");
          this.confirmDialog = false;
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", { message: err?.data?.detail || "Failed to change destination.", color: "error" });
        })
        .finally(() => { this.submitting = false; });
    },
  },
};
</script>

<style scoped>
.section-block {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
  border: 1px solid #e9ecef;
}
.section-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}
.info-item { font-size: 12px; }
.info-label { color: #888; font-weight: 400; }
.info-value { color: #2c3e50; font-weight: 600; }
.detail-label { font-size: 11px; color: #9e9e9e; text-transform: uppercase; letter-spacing: 0.5px; }
.detail-value { font-size: 13px; color: #2c3e50; font-weight: 500; margin-top: 2px; }
.rates-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.rates-table th {
  text-align: left; padding: 6px 10px;
  background: #eef2fb; color: #5c6bc0;
  font-weight: 600; font-size: 11px;
  text-transform: uppercase; letter-spacing: 0.4px;
  border-bottom: 1px solid #d5ddf0;
}
.rates-table td { padding: 6px 10px; color: #2c3e50; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.rates-table tbody tr:last-child td { border-bottom: none; }
.rates-table tbody tr:hover td { background: #f8f9fa; }
.rate-chip {
  display: inline-block; padding: 2px 10px;
  background: #e8f5e9; color: #2e7d32;
  border-radius: 12px; font-weight: 600; font-size: 12px;
}
.rate-chip--none { background: #fce4e4; color: #c62828; }
</style>
